import React, { useState, useEffect } from "react";
import axios from "axios";
import { config } from "../../../config";
import { 
  BookOpen, 
  FolderOpen, 
  FileText, 
  Plus, 
  Edit, 
  Trash2, 
  Save,
  X,
  ChevronDown,
  ChevronUp,
  Layers
} from "lucide-react";

const apiUrl = config.apiUrl;

const CreateSubject = ({ onSubjectCreated }) => {
  const [subjects, setSubjects] = useState([]);
  const [modules, setModules] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedModule, setSelectedModule] = useState(null);
  
  const [subjectForm, setSubjectForm] = useState({
    name: "",
    description: "",
  });
  
  const [moduleForm, setModuleForm] = useState({
    subject_id: "",
    name: "",
    description: "",
    order: 0
  });
  
  const [lessonForm, setLessonForm] = useState({
    module_id: "",
    title: "",
    content: "",
    order: 0,
    revision_number: 0
  });
  
  const [editingSubject, setEditingSubject] = useState(null);
  const [editingModule, setEditingModule] = useState(null);
  const [editingLesson, setEditingLesson] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState("subjects");
  const [expandedSubject, setExpandedSubject] = useState(null);

  useEffect(() => {
    fetchSubjects();
  }, []);

  useEffect(() => {
    if (selectedSubject) {
      fetchModules(selectedSubject.id);
    }
  }, [selectedSubject]);

  useEffect(() => {
    if (selectedModule) {
      fetchLessons(selectedModule.id);
    }
  }, [selectedModule]);

  const fetchSubjects = async () => {
    try {
      const response = await axios.get(`${apiUrl}/subjects`);
      setSubjects(response.data);
    } catch (err) {
      setError("Failed to load subjects");
      console.error("Error fetching subjects:", err);
    }
  };

  const fetchModules = async (subjectId) => {
    try {
      const response = await axios.get(`${apiUrl}/subjects/${subjectId}/modules`);
      setModules(response.data.data || []);
    } catch (err) {
      console.error("Error fetching modules:", err);
    }
  };

  const fetchLessons = async (moduleId) => {
    try {
      const response = await axios.get(`${apiUrl}/modules/${moduleId}/lessons`);
      setLessons(response.data.data || []);
    } catch (err) {
      console.error("Error fetching lessons:", err);
    }
  };

  const handleSubjectChange = (e) => {
    const { name, value } = e.target;
    setSubjectForm(prev => ({ ...prev, [name]: value }));
  };

  const handleModuleChange = (e) => {
    const { name, value } = e.target;
    setModuleForm(prev => ({
      ...prev,
      [name]: name === "order" ? parseInt(value) || 0 : value
    }));
  };

  const handleLessonChange = (e) => {
    const { name, value } = e.target;
    setLessonForm(prev => ({
      ...prev,
      [name]: name === "order" || name === "revision_number" ? parseInt(value) || 0 : value
    }));
  };

  const handleSubjectSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      if (editingSubject) {
        await axios.put(`${apiUrl}/subjects/${editingSubject.id}`, subjectForm);
        setSuccess("Subject updated successfully!");
      } else {
        await axios.post(`${apiUrl}/subjects`, subjectForm);
        setSuccess("Subject created successfully!");
      }
      
      await fetchSubjects();
      resetSubjectForm();

      setActiveTab("subjects")
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save subject");
      console.error("Error saving subject:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleModuleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      if (editingModule) {
        await axios.put(`${apiUrl}/modules/${editingModule.id}`, moduleForm);
        setSuccess("Module updated successfully!");
      } else {
        await axios.post(`${apiUrl}/modules`, moduleForm);
        setSuccess("Module created successfully!");
      }
      
      if (selectedSubject) {
        await fetchModules(selectedSubject.id);
      }
      resetModuleForm();
      setActiveTab("modules")
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save module");
      console.error("Error saving module:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLessonSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      if (editingLesson) {
        await axios.put(`${apiUrl}/lessons/${editingLesson.id}`, lessonForm);
        setSuccess("Lesson updated successfully!");
      } else {
        await axios.post(`${apiUrl}/lessons`, lessonForm);
        setSuccess("Lesson created successfully!");
      }
      
      if (selectedModule) {
        await fetchLessons(selectedModule.id);
      }
      resetLessonForm();

      setActiveTab("lessons");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save lesson");
      console.error("Error saving lesson:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditSubject = (subject) => {
    setEditingSubject(subject);
    setSubjectForm({
      name: subject.name,
      description: subject.description || ""
    });
    setActiveTab("subjectForm");
  };

  const handleEditModule = (module) => {
    setEditingModule(module);
    setModuleForm({
      subject_id: module.subject_id,
      name: module.name,
      description: module.description || "",
      order: module.order || 0
    });
    setActiveTab("moduleForm");
  };

  const handleEditLesson = (lesson) => {
    setEditingLesson(lesson);
    setLessonForm({
      module_id: lesson.module_id,
      title: lesson.title,
      content: lesson.content || "",
      order: lesson.order || 0,
      revision_number: lesson.revision_number || 0
    });
    setActiveTab("lessonForm");
  };

  const handleDeleteSubject = async (id) => {
    if (window.confirm("Are you sure you want to delete this subject? All modules and lessons will be deleted.")) {
      try {
        await axios.delete(`${apiUrl}/subjects/${id}`);
        setSuccess("Subject deleted successfully!");
        await fetchSubjects();
        if (selectedSubject && selectedSubject.id === id) {
          setSelectedSubject(null);
          setModules([]);
          setLessons([]);
        }
      } catch (err) {
        setError("Failed to delete subject");
        console.error("Error deleting subject:", err);
      }
    }
  };

  const handleDeleteModule = async (id) => {
    if (window.confirm("Are you sure you want to delete this module? All lessons will be deleted.")) {
      try {
        await axios.delete(`${apiUrl}/modules/${id}`);
        setSuccess("Module deleted successfully!");
        if (selectedSubject) {
          await fetchModules(selectedSubject.id);
        }
        if (selectedModule && selectedModule.id === id) {
          setSelectedModule(null);
          setLessons([]);
        }
      } catch (err) {
        setError("Failed to delete module");
        console.error("Error deleting module:", err);
      }
    }
  };

  const handleDeleteLesson = async (id) => {
    if (window.confirm("Are you sure you want to delete this lesson?")) {
      try {
        await axios.delete(`${apiUrl}/lessons/${id}`);
        setSuccess("Lesson deleted successfully!");
        if (selectedModule) {
          await fetchLessons(selectedModule.id);
        }
      } catch (err) {
        setError("Failed to delete lesson");
        console.error("Error deleting lesson:", err);
      }
    }
  };

  const resetSubjectForm = () => {
    setSubjectForm({ name: "", description: "" });
    setEditingSubject(null);
  };

  const resetModuleForm = () => {
    setModuleForm({
      subject_id: selectedSubject?.id || "",
      name: "",
      description: "",
      order: 0
    });
    setEditingModule(null);
  };

  const resetLessonForm = () => {
    setLessonForm({
      module_id: selectedModule?.id || "",
      title: "",
      content: "",
      order: 0,
      revision_number: 0
    });
    setEditingLesson(null);
  };

  const selectSubject = (subject) => {
    setSelectedSubject(subject);
    setModuleForm(prev => ({ ...prev, subject_id: subject.id }));
    setActiveTab("modules");
  };

  const selectModule = (module) => {
    setSelectedModule(module);
    setLessonForm(prev => ({ ...prev, module_id: module.id }));
    setActiveTab("lessons");
  };

  return (
    <div className="container mx-auto p-6">
      <h1 className="text-3xl font-bold mb-8 text-center text-gray-800">Content Management System</h1>
      
      {error && (
        <div className="mb-6 p-4 bg-red-100 text-red-700 rounded-lg border border-red-200">
          <div className="flex justify-between items-start">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="text-red-700 hover:text-red-900">
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {success && (
        <div className="mb-6 p-4 bg-green-100 text-green-700 rounded-lg border border-green-200">
          <div className="flex justify-between items-start">
            <span>{success}</span>
            <button onClick={() => setSuccess(null)} className="text-green-700 hover:text-green-900">
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Navigation Sidebar */}
        <div className="bg-white rounded-lg shadow-md p-4">
          <h2 className="text-xl font-bold mb-4">Navigation</h2>
          <nav className="space-y-2">
            <button
              onClick={() => setActiveTab("subjects")}
              className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                activeTab === "subjects" 
                  ? "bg-blue-100 text-blue-800 font-medium" 
                  : "text-gray-600 hover:bg-gray-100"
              }`}
            >
              <BookOpen size={18} className="inline mr-2" />
              Subjects
            </button>
            <button
              onClick={() => setActiveTab("subjectForm")}
              className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                activeTab === "subjectForm" 
                  ? "bg-blue-100 text-blue-800 font-medium" 
                  : "text-gray-600 hover:bg-gray-100"
              }`}
            >
              <Plus size={18} className="inline mr-2" />
              New Subject
            </button>
            {selectedSubject && (
              <>
                <button
                  onClick={() => setActiveTab("modules")}
                  className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                    activeTab === "modules" 
                      ? "bg-green-100 text-green-800 font-medium" 
                      : "text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  <FolderOpen size={18} className="inline mr-2" />
                  Modules
                </button>
                <button
                  onClick={() => setActiveTab("moduleForm")}
                  className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                    activeTab === "moduleForm" 
                      ? "bg-green-100 text-green-800 font-medium" 
                      : "text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  <Plus size={18} className="inline mr-2" />
                  New Module
                </button>
              </>
            )}
            {selectedModule && (
              <>
                <button
                  onClick={() => setActiveTab("lessons")}
                  className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                    activeTab === "lessons" 
                      ? "bg-purple-100 text-purple-800 font-medium" 
                      : "text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  <FileText size={18} className="inline mr-2" />
                  Lessons
                </button>
                <button
                  onClick={() => setActiveTab("lessonForm")}
                  className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                    activeTab === "lessonForm" 
                      ? "bg-purple-100 text-purple-800 font-medium" 
                      : "text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  <Plus size={18} className="inline mr-2" />
                  New Lesson
                </button>
              </>
            )}
          </nav>

          {/* Selected Items */}
          <div className="mt-6 p-3 bg-gray-50 rounded-lg">
            <h3 className="font-semibold mb-2">Selected:</h3>
            {selectedSubject && (
              <div className="mb-2">
                <span className="text-sm text-blue-600">Subject: {selectedSubject.name}</span>
              </div>
            )}
            {selectedModule && (
              <div>
                <span className="text-sm text-green-600">Module: {selectedModule.name}</span>
              </div>
            )}
          </div>
        </div>

        {/* Main Content Area */}
        <div className="lg:col-span-3">
          <div className="bg-white rounded-lg shadow-md p-6">
            {/* Subject Form */}
            {activeTab === "subjectForm" && (
              <div>
                <h2 className="text-2xl font-bold mb-6">
                  {editingSubject ? "Edit Subject" : "Create New Subject"}
                </h2>
                <form onSubmit={handleSubjectSubmit} className="space-y-4">
                  <div>
                    <label className="block text-gray-700 mb-2 font-medium">Name*</label>
                    <input
                      type="text"
                      name="name"
                      value={subjectForm.name}
                      onChange={handleSubjectChange}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                      placeholder="Enter subject name"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 mb-2 font-medium">Description</label>
                    <textarea
                      name="description"
                      value={subjectForm.description}
                      onChange={handleSubjectChange}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[100px]"
                      placeholder="Subject description"
                    />
                  </div>
                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => {
                        resetSubjectForm();
                        setActiveTab("subjects");
                      }}
                      className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    >
                      {isSubmitting ? "Saving..." : editingSubject ? "Update Subject" : "Create Subject"}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Subjects List */}
            {activeTab === "subjects" && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold">Subjects</h2>
                  <button
                    onClick={() => {
                      resetSubjectForm();
                      setActiveTab("subjectForm");
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
                  >
                    <Plus size={18} className="mr-2" />
                    New Subject
                  </button>
                </div>
                {subjects.length === 0 ? (
                  <div className="text-center text-gray-500 py-12">
                    <BookOpen size={48} className="mx-auto mb-4 text-gray-400" />
                    <p>No subjects found</p>
                    <p className="text-sm">Create your first subject to get started</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {subjects.map((subject) => (
                      <div
                        key={subject.id}
                        className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                        onClick={() => selectSubject(subject)}
                      >
                        <div className="flex justify-between items-start mb-3">
                          <h3 className="font-semibold text-lg text-gray-800">{subject.name}</h3>
                          <div className="flex space-x-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditSubject(subject);
                              }}
                              className="p-1 text-blue-600 hover:text-blue-800"
                              title="Edit subject"
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteSubject(subject.id);
                              }}
                              className="p-1 text-red-600 hover:text-red-800"
                              title="Delete subject"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                        {subject.description && (
                          <p className="text-gray-600 text-sm mb-3">{subject.description}</p>
                        )}
                        <p className="text-gray-600 text-sm mb-3">Subject id: {subject.id}</p>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            selectSubject(subject);
                          }}
                          className="w-full mt-2 px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                        >
                          View Modules
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Modules List */}
            {activeTab === "modules" && selectedSubject && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h2 className="text-2xl font-bold">Modules in {selectedSubject.name}</h2>
                    <p className="text-gray-600">Subject: {selectedSubject.name}</p>
                  </div>
                  <button
                    onClick={() => {
                      resetModuleForm();
                      setActiveTab("moduleForm");
                    }}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center"
                  >
                    <Plus size={18} className="mr-2" />
                    New Module
                  </button>
                </div>
                {modules.length === 0 ? (
                  <div className="text-center text-gray-500 py-12">
                    <FolderOpen size={48} className="mx-auto mb-4 text-gray-400" />
                    <p>No modules found in this subject</p>
                    <p className="text-sm">Create your first module to get started</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-4">
                    {modules.map((module) => (
                      <div
                        key={module.id}
                        className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                        onClick={() => selectModule(module)}
                      >
                        <div className="flex justify-between items-start mb-3">
                          <h3 className="font-semibold text-lg text-gray-800">{module.name}</h3>
                          <div className="flex space-x-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditModule(module);
                              }}
                              className="p-1 text-blue-600 hover:text-blue-800"
                              title="Edit module"
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteModule(module.id);
                              }}
                              className="p-1 text-red-600 hover:text-red-800"
                              title="Delete module"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                        {module.description && (
                          <p className="text-gray-600 text-sm mb-3">{module.description}</p>
                        )}
                        <div className="flex justify-between items-center text-xs text-gray-500">
                          <span>Module id: {module.id}</span>
                          <span>Order: {module.order}</span>
                          <span>{module.lessons?.length || 0} lessons</span>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            selectModule(module);
                          }}
                          className="w-full mt-2 px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                        >
                          View Lessons
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Module Form */}
            {activeTab === "moduleForm" && selectedSubject && (
              <div>
                <h2 className="text-2xl font-bold mb-6">
                  {editingModule ? "Edit Module" : "Create New Module"}
                </h2>
                <div className="bg-blue-50 p-3 rounded-lg mb-4">
                  <p className="text-blue-800">
                    <strong>Subject:</strong> {selectedSubject.name}
                  </p>
                </div>
                <form onSubmit={handleModuleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-gray-700 mb-2 font-medium">Module Name*</label>
                    <input
                      type="text"
                      name="name"
                      value={moduleForm.name}
                      onChange={handleModuleChange}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                      placeholder="Enter module name"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 mb-2 font-medium">Description</label>
                    <textarea
                      name="description"
                      value={moduleForm.description}
                      onChange={handleModuleChange}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[100px]"
                      placeholder="Module description"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 mb-2 font-medium">Order</label>
                    <input
                      type="number"
                      name="order"
                      value={moduleForm.order}
                      onChange={handleModuleChange}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      min="0"
                    />
                  </div>
                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => setActiveTab("modules")}
                      className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                    >
                      {isSubmitting ? "Saving..." : editingModule ? "Update Module" : "Create Module"}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Lessons List */}
            {activeTab === "lessons" && selectedModule && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h2 className="text-2xl font-bold">Lessons in {selectedModule.name}</h2>
                    <p className="text-gray-600">
                      Module: {selectedModule.name} | Subject: {selectedSubject.name} 
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      resetLessonForm();
                      setActiveTab("lessonForm");
                    }}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center"
                  >
                    <Plus size={18} className="mr-2" />
                    New Lesson
                  </button>
                </div>
                {lessons.length === 0 ? (
                  <div className="text-center text-gray-500 py-12">
                    <FileText size={48} className="mx-auto mb-4 text-gray-400" />
                    <p>No lessons found in this module</p>
                    <p className="text-sm">Create your first lesson to get started</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-4">
                    {lessons.map((lesson) => (
                      <div key={lesson.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                        <div className="flex justify-between items-start mb-3">
                          <h3 className="font-semibold text-lg text-gray-800">{lesson.title}</h3>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleEditLesson(lesson)}
                              className="p-1 text-blue-600 hover:text-blue-800"
                              title="Edit lesson"
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              onClick={() => handleDeleteLesson(lesson.id)}
                              className="p-1 text-red-600 hover:text-red-800"
                              title="Delete lesson"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                        {lesson.content && (
                          <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                            {lesson.content.replace(/<[^>]*>/g, "").substring(0, 150)}...
                          </p>
                        )}
                        <div className="flex justify-between items-center text-xs text-gray-500">
                          <span>Lesson Id: {lesson.id}</span>
                          <span>Order: {lesson.order}</span>
                          <span>Revision: {lesson.revision_number}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Lesson Form */}
            {activeTab === "lessonForm" && selectedModule && (
              <div>
                <h2 className="text-2xl font-bold mb-6">
                  {editingLesson ? "Edit Lesson" : "Create New Lesson"}
                </h2>
                <div className="bg-purple-50 p-3 rounded-lg mb-4">
                  <p className="text-purple-800">
                    <strong>Module:</strong> {selectedModule.name} | <strong>Subject:</strong> {selectedSubject.name}
                  </p>
                </div>
                <form onSubmit={handleLessonSubmit} className="space-y-4">
                  <div>
                    <label className="block text-gray-700 mb-2 font-medium">Lesson Title*</label>
                    <input
                      type="text"
                      name="title"
                      value={lessonForm.title}
                      onChange={handleLessonChange}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                      placeholder="Enter lesson title"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 mb-2 font-medium">Content</label>
                    <textarea
                      name="content"
                      value={lessonForm.content}
                      onChange={handleLessonChange}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[150px]"
                      placeholder="Lesson content (supports HTML formatting)"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-700 mb-2 font-medium">Order</label>
                      <input
                        type="number"
                        name="order"
                        value={lessonForm.order}
                        onChange={handleLessonChange}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        min="0"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 mb-2 font-medium">Revision Number</label>
                      <input
                        type="number"
                        name="revision_number"
                        value={lessonForm.revision_number}
                        onChange={handleLessonChange}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        min="0"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => setActiveTab("lessons")}
                      className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                    >
                      {isSubmitting ? "Saving..." : editingLesson ? "Update Lesson" : "Create Lesson"}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateSubject;